import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';

import { Config } from '../../common/config';
import { formatGiftSerialDisplay } from '../../utils/formatGiftSerial';
import AlertModal from '../../components/alertModal';
import LeftMenu from '../../components/leftMenu';
import Pagination from '../../components/pagination';
import {
  formatNumNumber,
  toSeoulDateString,
  getErrorMessage,
  isSuccessResponse,
} from '../../components/utils';
import translations from '../../data/translation';
import LocaleDateInput from '../../components/LocaleDateInput';
import useAxiosFetch from '../../hooks/useAxiosFetch'; // 커스텀 훅 임포트
import useStoreNo from '../../hooks/useStoreNo';

export default function GiftList() {
  const navigate = useNavigate();
  const userInfo = useSelector(state => state.userInfo);
  const language = useSelector(state => state.language.language); // redux에서 언어 상태 가져오기
  const t = translations[language]; // 현재 언어에 따른 번역 객체 가져오기

  const { fetchData } = useAxiosFetch();
  const storeNo = useStoreNo();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState({
    isCancel: false,
    isConfirm: true,
    isMessage: true,
    headerMsg: '',
    actionFlag: '',
    mainMsg: '',
    action: '',
    isPercent: false,
  });

  const [searchCondition, setSearchCondition] = useState({
    flag: false,
    pageSize: Config.PAGE_SIZE_LIST[0],
    sortOrd: '',
    startDttm: '',
    endDttm: '',
    giftIdOrd: '',
    saleOrd: '',
    state: '',
  });

  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);

  // 안전한 날짜 포매팅 (YYYY-MM-DD)
  function safeDateYYYYMMDD(value) {
    try {
      if (!value) return '';
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().substring(0, 10);
    } catch (_) {
      return '';
    }
  }

  const closeModal = () => {
    setModalOpen(false);
  };

  const doModal = () => {
    setModalOpen(false);
    if (modalInfo.actionFlag === 'goMain') {
      navigate('/go-main');
    }
  };

  const modGift = giftNo => {
    const passData = {
      giftNo: giftNo,
    };

    if (giftNo === 0) {
      navigate(`/${storeNo}/gift-content`);
    } else {
      navigate(`/${storeNo}/gift-manage`, { state: passData });
    }
  };

  // giftNo 기준 조회 — 서버가 같은 시리즈(이전·현재 버전)를 묶어서 돌려준다. 0이면 전체.
  const sales = giftNo => {
    const passData = {
      giftNo: giftNo,
    };

    navigate(`/${storeNo}/gift-sales`, { state: passData });
  };

  async function getData(pageOverride) {
    try {
      const pageNo = pageOverride !== undefined ? pageOverride : currentPage;

      let params = '?storeNo=' + userInfo.storeNo;

      params += '&historyYn=Y';
      params += '&pageNo=' + pageNo;
      params += '&pageSize=' + searchCondition.pageSize;
      if (searchCondition.sortOrd.length > 0) {
        params += '&sortOrd=' + searchCondition.sortOrd;
      }
      if (searchCondition.startDttm.length > 0) {
        params += '&startDttm=' + searchCondition.startDttm;
      }
      if (searchCondition.endDttm.length > 0) {
        params += '&endDttm=' + searchCondition.endDttm;
      }
      if (searchCondition.giftIdOrd.length > 0) {
        params += '&giftIdOrd=' + searchCondition.giftIdOrd;
      }
      if (searchCondition.saleOrd.length > 0) {
        params += '&saleOrd=' + searchCondition.saleOrd;
      }
      if (searchCondition.state.length > 0) {
        params += '&state=' + searchCondition.state;
      }

      const ret = await fetchData({
        url: Config.API_SERVER + 'gift' + params,
        method: 'get',
      });
      if (isSuccessResponse(ret) && ret?.data?.data) {
        const count = ret.data.data.total;

        const tp = Math.ceil(count / searchCondition.pageSize);

        setTotalPage(tp);
        setTotal(count);
        setData(ret.data.data.list);
      } else if (!isSuccessResponse(ret)) {
        setModalInfo(prev => ({
          ...prev,
          mainMsg: getErrorMessage(ret),
        }));
        setModalOpen(true);
      }
      setSearchCondition({ ...searchCondition, flag: false });
    } catch (error) {
      setModalInfo(prev => ({
        ...prev,
        mainMsg: getErrorMessage(error?.response),
      }));
      setModalOpen(true);
      console.error('Error getData:', error);
    }
  }

  const pageChange = page => {
    setCurrentPage(page);
  };

  // 검색 버튼 클릭 시 조건에 맞게 데이터 조회 (항상 1페이지부터)
  const getSearch = () => {
    setCurrentPage(1);
    getData(1);
  };

  // 초기화 버튼 클릭 시 검색조건만 초기화 (API 호출 X)
  const getClear = () => {
    setSearchCondition({
      flag: false,
      pageSize: Config.PAGE_SIZE_LIST[0],
      sortOrd: '',
      startDttm: '',
      endDttm: '',
      giftIdOrd: '',
      saleOrd: '',
      state: '',
    });
    setCurrentPage(1);
  };

  // 최초 렌더링 및 currentPage 변경 시 localStorage 기반 로그인 상태 점검 후 gift API 호출
  useEffect(() => {
    try {
      const persisted = localStorage.getItem('persist:root');
      if (!persisted) {
        navigate('/login', { replace: true });
        return;
      }
      const root = JSON.parse(persisted);
      const userInfoStored =
        root && root.userInfo ? JSON.parse(root.userInfo) : null;
      if (!userInfoStored || !userInfoStored.id) {
        navigate('/login', { replace: true });
        return;
      }
    } catch (err) {
      navigate('/login', { replace: true });
      return;
    }

    getData();
    // eslint-disable-next-line
  }, [currentPage]);

  return (
    <>
      <AlertModal
        open={modalOpen}
        close={closeModal}
        action={doModal}
        header={modalInfo.headerMsg}
        isConfirm={modalInfo.isConfirm}
        isMessage={modalInfo.isMessage}
        isCancel={modalInfo.isCancel}
      >
        {modalInfo.mainMsg}
      </AlertModal>

      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>
              {t.home}
            </NavLink>
          </li>
          <li>{t.giftManagement}</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="gift" />

          <section className="rightcontant_box">
            <h2 className="tit_01">{t.giftManagement}</h2>

            <article className="search_card">
              <div className="search_row">
                <div className="search_field">
                  <span className="field_label">{t.searchPeriod}</span>
                  <LocaleDateInput
                    value={searchCondition.startDttm}
                    onChange={val =>
                      setSearchCondition({
                        ...searchCondition,
                        startDttm: val,
                      })
                    }
                    placeholderText={
                      language === 'ko' ? '시작 날짜' : 'Start date'
                    }
                  />
                  <span className="date_sep">~</span>
                  <LocaleDateInput
                    value={searchCondition.endDttm}
                    onChange={val =>
                      setSearchCondition({
                        ...searchCondition,
                        endDttm: val,
                      })
                    }
                    placeholderText={
                      language === 'ko' ? '종료 날짜' : 'End date'
                    }
                  />
                </div>
                <div className="search_field">
                  <span className="field_label">{t.giftId}</span>
                  <select
                    value={searchCondition.giftIdOrd}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        giftIdOrd: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '기본순' : 'Default'}</option>
                    <option value="desc">{t.descendingOrder}</option>
                    <option value="asc">{t.ascendingOrder}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{t.saleQuantity}</span>
                  <select
                    value={searchCondition.saleOrd}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        saleOrd: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '기본순' : 'Default'}</option>
                    <option value="desc">{t.descendingOrder}</option>
                    <option value="asc">{t.ascendingOrder}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{t.state}</span>
                  <select
                    value={searchCondition.state}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        state: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '전체' : 'All'}</option>
                    <option value="SS001">{t.saleRegister}</option>
                    <option value="SS002">{t.saleStart}</option>
                    <option value="SS003">{t.salePause}</option>
                    <option value="SS004">{t.saleEnd}</option>
                    <option value="SS005">{t.saleCancel}</option>
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{t.registrationDate}</span>
                  <select
                    value={searchCondition.sortOrd}
                    onChange={event =>
                      setSearchCondition({
                        ...searchCondition,
                        sortOrd: event.target.value,
                      })
                    }
                  >
                    <option value="">{language === 'ko' ? '기본순' : 'Default'}</option>
                    <option value="desc">{t.descendingOrder}</option>
                    <option value="asc">{t.ascendingOrder}</option>
                  </select>
                </div>
                <div className="search_btns">
                  <button
                    type="button"
                    className="button_05 button_black"
                    onClick={getSearch}
                  >
                    {t.search}
                  </button>
                  <button
                    type="button"
                    className="button_05 button_gray_line"
                    onClick={getClear}
                  >
                    {t.reset}
                  </button>
                </div>
              </div>
            </article>

            {/* [2026-10-28] 목록을 테두리 카드로(상점 정보 관리 · 프로모션 관리와 같은 틀), 표는 gift-table 로 다듬음 */}
            <article className="form_card list_card">
            <table className="table_box_01 gift-table">
              <colgroup>
                <col width="56px" />
                <col />
                <col width="84px" />
                <col width="100px" />
                <col width="130px" />
                <col width="120px" />
                <col width="100px" />
                <col width="100px" />
                <col width="100px" />
                <col width="150px" />
              </colgroup>

              <thead>
                <tr>
                  <th>No</th>
                  {/* 값(왼쪽 정렬)과 같은 줄에 맞춘다 */}
                  <th className="gift-table__serial">{t.giftSerial}</th>
                  <th>{t.giftImage}</th>
                  <th>{t.price}</th>
                  <th>
                    {t.remainingCount}
                    <span className="gift-table__th-sub">/ {t.totalIssued}</span>
                  </th>
                  <th>{t.totalSalesAmount}</th>
                  <th>{t.state}</th>
                  <th>{t.registrationDate}</th>
                  <th>{t.saleDate}</th>
                  <th>{t.editOrSalesStatus}</th>
                </tr>
              </thead>

              <tbody>
                {data.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td className="gift-table__serial">
                      {formatGiftSerialDisplay(item)}
                    </td>
                    <td>
                      {/* 이미지가 없거나 깨지면 숨기고 회색 자리표시(gift-thumb 배경)를 보여 준다 */}
                      <div className="gift-thumb">
                        {item.thumbnailFileUrl && item.thumbnailFileUrl !== Config.API_SERVER && (
                          <img
                            src={item.thumbnailFileUrl}
                            alt={item.giftName}
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                    </td>
                    <td className="gift-table__num">
                      {formatNumNumber(item.price)}
                      {t.currency}
                    </td>
                    <td>
                      <div className="sold-issued-cell">
                        <div className="sold-issued-bar-wrap">
                          <div
                            className="sold-issued-bar-fill"
                            style={{
                              width: `${
                                item.countIssued && item.countIssued > 0
                                  ? Math.min(
                                      100,
                                      (item.countRemained / item.countIssued) * 100
                                    )
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                        <div className="sold-issued-nums">
                          <span className="sold-num">
                            {formatNumNumber(item.countRemained)}
                          </span>
                          <span className="sold-issued-sep">/</span>
                          <span className="issued-num">
                            {item.countIssued === null ? (
                              <span className="cell-empty">—</span>
                            ) : (
                              formatNumNumber(item.countIssued)
                            )}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="gift-table__num">
                      {formatNumNumber(item.totalSaleAmount || 0)}
                      {t.currency}
                    </td>
                    <td>
                      <span
                        className={`state-badge state-${item.state || 'SS001'}`}
                      >
                        {t.stateDesc[item.stateDesc] || item.stateDesc}
                      </span>
                    </td>
                    <td className="gift-table__date">{safeDateYYYYMMDD(item.regDttm)}</td>
                    <td className="gift-table__date">{toSeoulDateString(item.saleStartDttm)}</td>
                    <td className="gift-table__actions">
                      <button
                        type="button"
                        className="button_04 button_gray"
                        disabled={item.channelInternalYn === 'Y'}
                        title={
                          item.channelInternalYn === 'Y'
                            ? '채널에서 발행된 기프트는 이 화면에서 수정할 수 없습니다'
                            : undefined
                        }
                        onClick={() => {
                          modGift(item.giftNo);
                        }}
                      >
                        {t.edit}
                      </button>
                      <button
                        type="button"
                        className="button_04 button_gray_line"
                        onClick={() => {
                          sales(item.giftNo);
                        }}
                      >
                        {t.status}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="table_01">
              {t.totalCount} : {total} {t.count}
            </p>

            <ul className="page_box">
              <Pagination totalPage={totalPage} onPageChange={pageChange} />
            </ul>
            </article>

            <article className="button_box_02">
              <div>
                <button
                  type="button"
                  className="button_01 button_03 button_black_line"
                  onClick={() => {
                    sales(0);
                  }}
                >
                  {t.salesStatus}
                </button>
                <button
                  type="button"
                  className="button_01 button_001 button_03 button_black_line "
                  onClick={() => {
                    navigate(`/${storeNo}/gift-refund`);
                  }}
                >
                  {t.refundHistory}
                </button>
              </div>
              <button
                type="button"
                className="button_01 button_03 button_red"
                onClick={() => {
                  modGift(0);
                }}
              >
                {t.registerGift}
              </button>
            </article>
          </section>
        </section>
      </main>
    </>
  );
}
